import { useEffect, useRef, useState } from 'react'
import { APP_NAME, APP_VERSION } from '@/config'
import { sendFeedback } from '@/data/feedback'
import { describeDevice, KINDS, MAX_TEXT, MIN_TEXT, reportId, type FeedbackKind } from '@/lib/send-feedback'
import { isStandalone } from '@/lib/pwa'
import { usePrefs } from '@/store/prefs'
import { Button } from './Button'
import { Segmented } from './Segmented'
import { Sheet } from './Sheet'
import './FeedbackSheet.css'

const VIEW_NAMES = { plan: 'Plan', calendar: 'Calendar' } as const

interface FeedbackSheetProps {
  open: boolean
  onClose: () => void
}

/**
 * Settings → Send feedback: a bug report or an idea, filed as an issue in the app's GitHub repo. The draft
 * stays while Settings is open, so closing the sheet by mistake loses nothing, and a send carries on with the
 * sheet closed: it reopens on "Sending…", the thanks or the error.
 */
export function FeedbackSheet({ open, onClose }: FeedbackSheetProps) {
  const view = usePrefs((s) => s.view)
  const [kind, setKind] = useState<FeedbackKind>('bug')
  const [text, setText] = useState('')
  const [include, setInclude] = useState(true)
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle')
  const [error, setError] = useState<string | null>(null)
  // one id for these words, however often Send is pressed, so a reply lost on the way never files them twice
  const id = useRef<string | null>(null)
  const box = useRef<HTMLTextAreaElement>(null)
  // Send more puts the cursor in the empty box, ready for the next one; opening the sheet does not
  const focusBox = useRef(false)

  useEffect(() => {
    if (state !== 'idle' || !focusBox.current) return
    focusBox.current = false
    box.current?.focus()
  }, [state])

  const phone = typeof window !== 'undefined' && window.matchMedia('(max-width: 899px)').matches
  const details: Record<string, string> = {
    Version: APP_VERSION,
    Device: describeDevice(navigator.userAgent),
    Layout: `${phone ? 'phone' : 'wide'}${isStandalone() ? ', installed' : ''}`,
    'Last view': VIEW_NAMES[view],
  }
  const line = [`${APP_NAME} ${APP_VERSION}`, details.Device, details.Layout, details['Last view']].join(' · ')

  const close = () => {
    if (state === 'sent') setState('idle')
    setError(null)
    onClose()
  }

  // the kind and "Include app details" stay as they were: a second report is often the same sort as the first
  const sendMore = () => {
    focusBox.current = true
    setState('idle')
  }

  const send = async () => {
    setState('sending')
    setError(null)
    id.current ??= reportId()
    try {
      await sendFeedback({ kind, text, id: id.current, ...(include ? { details } : {}) })
      setText('')
      id.current = null
      setState('sent')
    } catch (e) {
      const code = (e as { code?: string }).code ?? ''
      setError(
        code.endsWith('resource-exhausted')
          ? "That's a lot of feedback in an hour. Try again a little later."
          : "That didn't send. Check your connection and try again; your words are still here.",
      )
      setState('idle')
    }
  }

  return (
    <Sheet open={open} heading="Send feedback" variant="panel" onClose={close}>
      {state === 'sent' ? (
        <div className="feedback__sent">
          <p>Thanks, that's sent. It goes straight to the person who builds {APP_NAME}.</p>
          <div className="feedback__actions">
            <Button variant="secondary" size="lg" block onClick={sendMore}>
              Send more
            </Button>
            <Button variant="primary" size="lg" block onClick={close}>
              Done
            </Button>
          </div>
        </div>
      ) : (
        <div className="feedback">
          <p className="feedback__lead">Something broken, or an idea? It goes straight to the person who builds {APP_NAME}.</p>
          <Segmented<FeedbackKind>
            label="Kind of feedback"
            value={kind}
            options={(Object.keys(KINDS) as FeedbackKind[]).map((k) => ({ value: k, label: KINDS[k].label }))}
            onChange={(k) => {
              id.current = null
              setKind(k)
            }}
          />
          <label className="sr-only" htmlFor="feedback-text">
            Your feedback
          </label>
          <textarea
            id="feedback-text"
            ref={box}
            className="feedback__text"
            rows={6}
            maxLength={MAX_TEXT}
            placeholder={KINDS[kind].prompt.replace('the app', APP_NAME)}
            value={text}
            onChange={(e) => {
              id.current = null
              setText(e.target.value)
            }}
          />
          <label className="feedback__check">
            <input type="checkbox" checked={include} onChange={(e) => setInclude(e.target.checked)} />
            Include app details
          </label>
          <p className={`feedback__line ${include ? '' : 'feedback__line--off'}`}>{line}</p>
          {error && (
            <p className="feedback__error" role="alert">
              {error}
            </p>
          )}
          <Button variant="primary" size="lg" block disabled={state === 'sending' || text.trim().length < MIN_TEXT} onClick={() => void send()}>
            {state === 'sending' ? 'Sending…' : 'Send'}
          </Button>
        </div>
      )}
    </Sheet>
  )
}
